import React, { useState } from 'react';
import { X, Plus, Trash2, Star } from 'lucide-react';
import { toKobo, toNaira, parsePrice } from '../utils/currency';

interface Color {
  name: string;
  hex: string;
  image: string;
}

interface EditProductModalProps {
  product: {
    id: string;
    name: string;
    price: number;
    image: string;
    colors?: Color[];
    rating?: number;
    is_available?: boolean;
  };
  onClose: () => void;
  onUpdate: () => Promise<void>;
}

export default function EditProductModal({ product, onClose, onUpdate }: EditProductModalProps) {
  const [name, setName] = useState(product.name);
  const [price, setPrice] = useState(String(toNaira(product.price)));
  const [colors, setColors] = useState<Color[]>(product.colors || []);
  const [rating, setRating] = useState(product.rating || 0);
  const [isAvailable, setIsAvailable] = useState(product.is_available ?? true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parsePrice(e.target.value);
    setPrice(newValue);
  };

  const handleAddColor = () => {
    setColors([...colors, { name: '', hex: '#000000', image: '' }]);
  };

  const handleRemoveColor = (index: number) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  const handleColorChange = (index: number, field: keyof Color, value: string) => {
    const newColors = [...colors];
    newColors[index] = { ...newColors[index], [field]: value };
    setColors(newColors);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-product`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: product.id }),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete product');
      }

      await onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-product`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: product.id,
          name,
          price: toKobo(Number(price)), // Convert string price to number
          colors,
          rating: Number(rating),
          is_available: isAvailable,
        }),
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update product');
      }

      await onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
      console.error('Error updating product:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Edit Product</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Product Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Price (₦)</label>
              <input
                type="text"
                value={price}
                onChange={handlePriceChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Rating (0-5)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={rating}
                  onChange={(e) => setRating(Math.min(5, Math.max(0, Number(e.target.value))))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                  max="5"
                  step="0.1"
                />
                <Star className="h-5 w-5 text-yellow-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Availability</label>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    checked={isAvailable}
                    onChange={() => setIsAvailable(true)}
                    className="form-radio h-4 w-4 text-purple-600"
                  />
                  <span className="ml-2">Available</span>
                </label>
                <label className="inline-flex items-center ml-6">
                  <input
                    type="radio"
                    checked={!isAvailable}
                    onChange={() => setIsAvailable(false)}
                    className="form-radio h-4 w-4 text-red-600"
                  />
                  <span className="ml-2">Sold Out</span>
                </label>
              </div>
            </div>
          </div>

          {/* Colors section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium">Color Options</label>
              <button
                type="button"
                onClick={handleAddColor}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
              >
                <Plus className="h-4 w-4" />
                Add Color
              </button>
            </div>

            <div className="space-y-4">
              {colors.map((color, index) => (
                <div key={index} className="flex gap-4 items-start p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Color Name</label>
                      <input
                        type="text"
                        value={color.name}
                        onChange={(e) => handleColorChange(index, 'name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                        placeholder="e.g., Black"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Color Code</label>
                      <input
                        type="color"
                        value={color.hex}
                        onChange={(e) => handleColorChange(index, 'hex', e.target.value)}
                        className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Image URL</label>
                      <input
                        type="url"
                        value={color.image}
                        onChange={(e) => handleColorChange(index, 'image', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveColor(index)}
                    className="mt-8 text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}

              {colors.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No color options added yet</p>
                  <button
                    type="button"
                    onClick={handleAddColor}
                    className="mt-2 text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Add your first color option
                  </button>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={handleDelete}
              className={`px-4 py-2 ${
                deleteConfirm 
                  ? 'bg-red-600 text-white' 
                  : 'border border-red-600 text-red-600'
              } rounded-lg hover:bg-red-700 hover:text-white transition-colors`}
              disabled={loading}
            >
              {deleteConfirm ? 'Confirm Delete' : 'Delete Product'}
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}